import type {
  OfficerActionQueueMutationBody,
  OfficerActionQueueMutationSnapshot,
} from '@/lib/officerActionQueueClient';

export const WRAP_UP_AI_TIMEOUT_MS = 15_000;
/**
 * How long the caller is given to answer a spoken wrap-up item before the
 * next one. The AI officer sequences speech in queue order after confirmed
 * playout, then waits for a caller turn or this answer window. All four
 * items remain visible while it waits.
 */
export const WRAP_UP_CALLER_RESPONSE_WAIT_MS = 8_000;

// ── Wrap-up order ──────────────────────────────────────────────────────
//
// The canonical action ids the Wrap Up button adds, in the order the officer
// closes a call. GOODBYE IS ALWAYS LAST (owner, 2026-09-10 / 09-23). Every
// ordering seam ranks wrap-up items by this one order, right after recording
// consent and ahead of every other item: the officer display order
// (lib/callActions.ts), and its mirrors in agent-caller officer_assist
// `_WRAP_UP_ACTION_ORDER` and api-ai-engine prioritization_handler
// `WRAP_UP_ACTION_ORDER`.

export const WRAP_UP_ACTION_ID_PREFIX = 'system-wrap-up-';
export const ANY_QUESTIONS_ACTION_ID = 'system-wrap-up-any-questions';
export const NEXT_STEPS_ACTION_ID = 'system-wrap-up-next-steps';
export const SUPPORT_RESOURCES_ACTION_ID = 'system-wrap-up-support-resources';
export const GOODBYE_ACTION_ID = 'system-wrap-up-goodbye';

export const WRAP_UP_ACTION_ORDER = [
  ANY_QUESTIONS_ACTION_ID,
  NEXT_STEPS_ACTION_ID,
  SUPPORT_RESOURCES_ACTION_ID,
  GOODBYE_ACTION_ID,
] as const;

/**
 * Position of a wrap-up item in the wrap-up order, or null for any other id.
 * An unknown `system-wrap-up-*` id ranks just before goodbye, so goodbye stays last.
 */
export function wrapUpActionRank(actionId: string | null | undefined): number | null {
  if (typeof actionId !== 'string' || !actionId.startsWith(WRAP_UP_ACTION_ID_PREFIX)) return null;
  const index = (WRAP_UP_ACTION_ORDER as readonly string[]).indexOf(actionId);
  return index >= 0 ? index : WRAP_UP_ACTION_ORDER.length - 1.5;
}

// ── Which wrap-up item to ask now ──────────────────────────────────────
//
// Andrew, 2026-10-28: "show all 4, but ordered/prioritized appropriately so
// they are asked in order." All four items are on screen together; the first
// one not yet addressed is the one to ask now, the rest wait behind it.

export type WrapUpItemState = 'current' | 'queued' | 'done';

/** The fields that say an item has been dealt with. */
export interface WrapUpAddressedFields {
  officerAddressed?: boolean | null;
  answerStatus?: string | null;
  answered?: boolean | null;
  spokenAt?: string | null;
}

/**
 * Has this wrap-up item been dealt with: marked answered, said by the officer,
 * marked complete, or read aloud by the AI officer (spokenAt)? Removed items
 * are simply absent from the list, so they never hold the order up.
 */
export function isWrapUpItemAddressed(
  id: string,
  answeredPrompts: readonly string[],
  copies: ReadonlyArray<WrapUpAddressedFields | null | undefined>,
): boolean {
  if (answeredPrompts.includes(id)) return true;
  return copies.some((copy) =>
    Boolean(
      copy &&
        (copy.officerAddressed || copy.answerStatus === 'complete' || copy.answered || copy.spokenAt),
    ),
  );
}

/**
 * The state of each wrap-up item in `ids` (other ids are ignored), walked in
 * WRAP_UP_ACTION_ORDER: addressed items are `done`, the first open item is
 * `current`, every later open item is `queued`. Addressing goodbye early
 * never makes goodbye current while an earlier item is open.
 */
export function wrapUpItemStates(
  ids: readonly string[],
  isAddressed: (id: string) => boolean,
): Map<string, WrapUpItemState> {
  const ordered = ids
    .filter((id) => wrapUpActionRank(id) !== null)
    .sort((a, b) => (wrapUpActionRank(a) as number) - (wrapUpActionRank(b) as number));
  const states = new Map<string, WrapUpItemState>();
  let currentTaken = false;
  for (const id of ordered) {
    if (isAddressed(id)) {
      states.set(id, 'done');
    } else if (!currentTaken) {
      states.set(id, 'current');
      currentTaken = true;
    } else {
      states.set(id, 'queued');
    }
  }
  return states;
}

export interface WrapUpQueueItem {
  id: string;
  type: 'question' | 'suggestion';
  details: string;
  priority: number;
}

/**
 * Hand a wrap-up item to the AI officer, after its card exists.
 *
 * The AI officer speaks ONLY from the call's speech queue, and a required
 * `system-*` row is protected: the AI queue manager may not add it. So a
 * wrap-up card that only exists as a call action is never spoken (staging
 * homu-dogu-lase-8178, 2026-10-26: any-questions sat unspoken for 14 minutes
 * until the officer queued it by hand). When the AI officer is speaking for
 * the officer, the item goes into the speech queue through the same
 * `officer_add` the officer's own "speak this" uses; otherwise the officer
 * reads it and nothing is queued.
 */
export async function handWrapUpItemToAiOfficer(input: {
  callId: string | null | undefined;
  item: WrapUpQueueItem;
  autoResponseEnabled: boolean;
  post: (
    callId: string,
    body: OfficerActionQueueMutationBody,
  ) => Promise<OfficerActionQueueMutationSnapshot>;
}): Promise<OfficerActionQueueMutationSnapshot | null> {
  const callId = input.callId?.trim();
  if (!callId || !input.autoResponseEnabled) return null;
  if (wrapUpActionRank(input.item.id) === null) return null;
  return input.post(callId, {
    operation: 'officer_add',
    item: {
      id: input.item.id,
      text: input.item.details,
      type: input.item.type,
      priority: input.item.priority,
    },
  });
}

// ── AI response types ──────────────────────────────────────────────────

export interface WrapUpActionPayload {
  action_key: 'any_questions' | 'next_steps' | 'support_resources';
  text: string;
  reason: string;
}

export interface WrapUpResponsePayload {
  actions: WrapUpActionPayload[];
}

// ── Fallback builders (used when AI call fails) ────────────────────────

export function buildFallbackAnyQuestionsText(): string {
  return 'Before we wrap up, do you have any questions about anything we\'ve discussed today?';
}

export function buildFallbackSupportResourcesText(): string {
  return 'If you would like help finding support resources, please let us know or contact our non-emergency line.';
}

export function buildFallbackNextStepsText(
  caseNumber?: string | null,
  officerName?: string | null,
  badgeNumber?: string | null,
  // Retain the existing call signature; an incident label is not timing policy.
  _incidentType?: string | null,
): string {
  const caseRef = caseNumber ? `Your case number is ${caseNumber}. ` : '';
  const nameRef = officerName || 'an officer';
  const badgeRef = badgeNumber ? `, badge number ${badgeNumber}` : '';
  return `${caseRef}An officer will review the information you've provided. If you need to reach us, you can call back and ask for ${nameRef}${badgeRef}.`;
}

/**
 * The name the caller is told to ask for. Owner, 2026-09-23: "your contact for
 * this matter is Officer Kelly ... <--- should be full name and badge number."
 * The in-call display name is "Officer <last name>", so the profile's full name
 * wins whenever the pre-join form captured it.
 */
export function wrapUpOfficerName(
  preJoin?: { officerFullName?: string | null; officerName?: string | null } | null,
): string | null {
  return preJoin?.officerFullName?.trim() || preJoin?.officerName?.trim() || null;
}
